/**
 * SupervisorOverrideModal — 3-tab modal opened when a supervisor scans
 * their badge on the operator screen. Provides: label reprints,
 * manual weight mode toggle, and end-of-day yield sign-off.
 * All actions are recorded to production_session_history.
 */
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { getPrintErrorMessage } from '../../../utils/printErrors';
import operatorService, { ProductionSession } from '../../../services/operatorService';
import productionService from '../../../services/productionService';
import { wsService } from '../../../services/websocket';
import stationManagementService from '../../../services/stationManagementService';
import barcodeService, { computeSerialNumber } from '../../../services/barcodeService';
import { getDateCode, formatElapsedTime } from '../../../utils/dateUtils';
import { generateLabelPreviewHtml, LabelData } from '../../../services/labelPrintService';
import LabelPreviewModal from '../../../components/LabelPreviewModal';
import LabelReprintsTab from './LabelReprintsTab';
import EndOfDaySummaryTab from './EndOfDaySummaryTab';
import toast from 'react-hot-toast';
import { X, Printer, Scale, ClipboardCheck, CheckCircle } from 'lucide-react';

interface SupervisorOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  supervisorId: string;
  supervisorName: string;
  supervisorEmployeeNumber?: string;
  session: ProductionSession;
  operatorEmployeeNumber?: string;
  operatorName?: string;
  onManualWeightToggle: (mode: 'fixed' | 'catch' | 'scale') => void;
  hideEodSummary?: boolean; // Hide End of Day Summary tab (for mobile packer view)
}

type TabType = 'reprints' | 'manualWeight' | 'eodSummary';

interface Box {
  id: string;
  box_number: string;
  original_box_number?: string;
  actual_weight: number;
  box_status: string;
  packed_at: string;
  voided_at?: string;
  label_printed_at?: string;
  packed_by_name: string;
  total_prints: number;
  barcode_number: string;
}

const SupervisorOverrideModal: React.FC<SupervisorOverrideModalProps> = ({
  isOpen,
  onClose,
  supervisorId,
  supervisorName,
  supervisorEmployeeNumber,
  session,
  operatorEmployeeNumber,
  operatorName,
  onManualWeightToggle,
  hideEodSummary = false,
}) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabType>('reprints');

  // Label reprints state
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [discardedLabels, setDiscardedLabels] = useState<Box[]>([]);
  const [loadingBoxes, setLoadingBoxes] = useState(false);
  const [reprintingBoxId, setReprintingBoxId] = useState<string | null>(null);
  const [reprintReason, setReprintReason] = useState('');
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [voidingBoxId, setVoidingBoxId] = useState<string | null>(null);

  // Weight mode state: 'fixed' | 'catch' | 'scale'
  const [activeWeightMode, setActiveWeightMode] = useState<'fixed' | 'catch' | 'scale'>('fixed');
  const [manualWeightReason, setManualWeightReason] = useState('');

  // EOD summary state
  const [signedOff, setSignedOff] = useState(false);
  const [signingOff, setSigningOff] = useState(false);

  // Resolved printer MAC/IP address for this station
  const [printerMac, setPrinterMac] = useState<string | null>(null);
  const [printerIp, setPrinterIp] = useState<string | null>(null);
  // Station location code for dynamic company info on labels (e.g. 'HTE', 'TNB', 'DSK')
  const [locationCode, setLocationCode] = useState<string | null>(null);

  // Label copies setting (from DB) + printer MAC resolution + simulate printer
  const [numberOfCopies, setNumberOfCopies] = useState(1);
  const [simulatePrinter, setSimulatePrinter] = useState(false);
  useEffect(() => {
    // Fetch config settings and station printers in parallel (not nested) to avoid silent failures
    const promises: [Promise<any>, Promise<any>] = [
      productionService.getConfigSettings(),
      session?.station_id ? stationManagementService.getStationPrinters() : Promise.resolve([]),
    ];
    Promise.all(promises)
      .then(([settings, stations]) => {
        const lc = settings.find((s: any) => s.setting_key === 'label_copies');
        if (lc) setNumberOfCopies(Number(lc.setting_value) || 2);
        const sp = settings.find((s: any) => s.setting_key === 'simulate_printer');
        setSimulatePrinter(sp?.setting_value === 'true');

        // Resolve printer MAC/IP from station's printer config key
        // printer_mac_id may point to base key ("printer_station-1") or IP key ("printer_station-1_ip")
        // Normalize to base key so we always find the correct _ip setting
        if (session?.station_id) {
          const stationPrinter = stations.find((s: any) => s.station_id === session.station_id);
          if (stationPrinter?.location_code) {
            setLocationCode(stationPrinter.location_code);
          }
          if (stationPrinter?.printer_mac_id) {
            const baseKey = stationPrinter.printer_mac_id.replace(/_ip$/, '');
            const macEntry = settings.find((s: any) => s.setting_key === baseKey);
            if (macEntry?.setting_value) {
              setPrinterMac(macEntry.setting_value);
            }
            const ipEntry = settings.find((s: any) => s.setting_key === baseKey + '_ip');
            if (ipEntry?.setting_value) {
              setPrinterIp(ipEntry.setting_value);
            }
          }
        }
      })
      .catch((err) => {
        console.warn('Failed to load printer config for supervisor override modal', err);
        toast.error(t('messages.loadFailed').replace('{item}', 'printer config'));
      });

    const handleConfigUpdate = (data: any) => {
      if (data?.key === 'label_copies') setNumberOfCopies(Number(data.value) || 2);
    };
    wsService.on('config-updated', handleConfigUpdate);
    return () => { wsService.off('config-updated', handleConfigUpdate); };
  }, [session?.station_id]);

  // Label preview state (shown when Simulate Printer is ON)
  const [showLabelPreview, setShowLabelPreview] = useState(false);
  const [labelPreviewHtml, setLabelPreviewHtml] = useState('');

  // Fresh session data for EOD summary (fetched when modal opens)
  const [freshSession, setFreshSession] = useState<ProductionSession | null>(null);

  // Fetch boxes and fresh session data when modal opens
  useEffect(() => {
    if (isOpen && session) {
      const sessionKey = session.sub_session_number || String(session.session_number);
      fetchBoxes(sessionKey);
      // Fetch fresh session data for accurate EOD summary
      if (session.sub_session_number) {
        fetchFreshSessionData(session.sub_session_number);
      }
    }
  }, [isOpen, session?.sub_session_number, session?.session_number]);

  const fetchFreshSessionData = async (subSessionNumber: string) => {
    try {
      const result = await operatorService.getSubSessionDetails(subSessionNumber);
      if (result.session) {
        setFreshSession(result.session);
      }
    } catch (err) {
      console.error('Failed to fetch fresh session data:', err);
    }
  };

  // Reset state when modal opens - initialize weight mode from session
  useEffect(() => {
    if (isOpen) {
      setActiveTab('reprints');
      setReprintReason('');
      setSelectedBoxId(null);
      setFreshSession(null); // Reset fresh session to force re-fetch
      // Initialize from session's current weight mode
      setActiveWeightMode(session?.weight_mode || 'fixed');
      setManualWeightReason('');
      setSignedOff(false);
    }
    // Only run when modal opens, NOT when weight_mode changes (to avoid tab reset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchBoxes = async (sessionKey: string) => {
    setLoadingBoxes(true);
    try {
      const data = await operatorService.getSessionBoxes(sessionKey, 'all');
      setBoxes(data.boxes || []);
      setDiscardedLabels(data.discarded_labels || []);
    } catch (err) {
      console.error('Failed to fetch boxes:', err);
    } finally {
      setLoadingBoxes(false);
    }
  };

  const handleVoidLabel = async (boxId: string) => {
    setVoidingBoxId(boxId);
    try {
      await operatorService.voidPendingLabel(boxId, supervisorEmployeeNumber || supervisorId);
      toast.success(t('supervisorOverride.voidSuccess'));
      // Refresh boxes list
      const sessionKey = session.sub_session_number || session.session_number;
      fetchBoxes(String(sessionKey));
    } catch (err) {
      console.error('Failed to void label:', err);
      toast.error(t('supervisorOverride.voidFailed'));
    } finally {
      setVoidingBoxId(null);
    }
  };

  const handleReprint = async (box: Box) => {
    if (!reprintReason) {
      toast.error(t('supervisorOverride.selectReason'));
      return;
    }

    setReprintingBoxId(box.id);
    try {
      // Generate new barcode data for the reprint label
      const dateCode = getDateCode();
      const serialNumber = computeSerialNumber();
      const barcodeData = await barcodeService.generateBarcode(
        session.product_sku,
        false,
        box.actual_weight,
        dateCode,
        serialNumber
      );

      // Send to Zebra printer via backend print-service with "REPRINTED" banner
      // Only record the reprint in database if print succeeds
      let printSucceeded = false;

      // Simulate printer mode: show label preview instead of printing
      if (simulatePrinter) {
        // Generate label preview HTML for TEST MODE (with REPRINTED banner)
        const now = new Date();
        const labelData: LabelData = {
          product_name: session.product_name,
          product_chinese: session.product_chinese || '',
          product_sku: session.product_sku,
          product_ingredient: session.product_ingredient || '',
          lot_number: session.raw_material_lot,
          box_number: Number(box.box_number) || 0,
          barcode_number: barcodeData?.data || '',
          actual_weight: Number(box.actual_weight).toFixed(2),
          target_weight: Number(session.product_unit_weight || box.actual_weight).toFixed(2),
          weight_unit: 'lbs',
          packed_date: now.toLocaleDateString(),
          packed_time: now.toLocaleTimeString(),
          packed_timestamp: now.toISOString(),
          operator_id: operatorEmployeeNumber || '',
          operator_name: operatorName || '',
          station_number: session.station_id || 0,
          station_name: session.station_name || '',
          raw_material_lot: session.raw_material_lot,
          raw_material_type: '',
          production_location: locationCode || 'HTE',
          qr_data: '',
        };
        const previewHtml = await generateLabelPreviewHtml(labelData, barcodeData, true); // true = isReprint
        setLabelPreviewHtml(previewHtml);
        setShowLabelPreview(true);
        toast.success('TEST MODE: Print skipped - showing preview');
        printSucceeded = true;
      } else {
        const now = new Date();
        try {
          const printResult = await operatorService.printLabel({
            label_data: {
              product_name: session.product_name,
              product_chinese: session.product_chinese || '',
              product_sku: session.product_sku,
              product_ingredient: session.product_ingredient || '',
              lot_number: session.raw_material_lot,
              box_number: box.box_number,
              barcode_number: barcodeData?.data || '',
              actual_weight: Number(box.actual_weight).toFixed(2),
              target_weight: Number(session.product_unit_weight || box.actual_weight).toFixed(2),
              weight_unit: 'lbs',
              packed_date: now.toLocaleDateString(),
              packed_time: now.toLocaleTimeString(),
              packed_timestamp: now.toISOString(),
              operator_id: operatorEmployeeNumber || '',
              operator_name: operatorName || '',
              station_number: session.station_id || 0,
              station_name: session.station_name || '',
              raw_material_lot: session.raw_material_lot,
              raw_material_type: '',
              production_location: locationCode || 'HTE',
            },
            barcode_data: barcodeData?.data || '',
            barcode_display_text: barcodeData?.displayText || barcodeData?.data || '',
            copies: numberOfCopies,
            is_reprint: true,
            station_id: session.station_id || undefined,
            printer_mac: printerMac || undefined,
            printer_ip: printerIp || undefined,
          });
          if (printResult.success) {
            printSucceeded = true;
          } else {
            console.warn('Reprint label print failed:', printResult.error);
            toast.error(getPrintErrorMessage(t, printResult.error_code, printResult.error, t('supervisorOverride.reprintPrintFailed')));
          }
        } catch (printErr: any) {
          console.warn('Reprint print service error:', printErr.message);
          const errData = printErr.response?.data;
          toast.error(getPrintErrorMessage(t, errData?.error_code, errData?.error, t('supervisorOverride.printerUnavailable')));
        }
      }

      // Only record reprint if print succeeded
      if (printSucceeded) {
        // Call reprint endpoint — records the reprint in database
        await operatorService.reprintLabel(box.id, {
          supervisor_id: supervisorId,
          reason: reprintReason,
          label_data: {
            product_name: session.product_name,
            product_chinese: session.product_chinese || '',
            product_sku: session.product_sku,
            weight: box.actual_weight,
            box_number: box.box_number,
            barcode: barcodeData?.data || '',
            displayText: barcodeData?.displayText || '',
          },
        });

        // Record the override in session history
        const parentSessionNumber = session.sub_session_number?.split('-')[0] || String(session.session_number);
        await operatorService.recordSupervisorOverride({
          override_type: 'label_reprint',
          supervisor_id: supervisorId,
          supervisor_name: supervisorName,
          session_number: parentSessionNumber,
          sub_session_number: session.sub_session_number,
          operator_employee_number: operatorEmployeeNumber,
          operator_name: operatorName,
          reason: reprintReason,
          details: {
            box_id: box.id,
            box_number: box.box_number,
            barcode: barcodeData?.data || '',
            product_number: session.product_sku,
            product_name: session.product_name,
          },
        });

        toast.success(t('supervisorOverride.reprintSuccess'));
        setReprintReason('');
        setSelectedBoxId(null);
        // Refresh box list to show updated reprint count
        const sessionKey = session.sub_session_number || String(session.session_number);
        fetchBoxes(sessionKey);
      }
    } catch (err) {
      console.error('Reprint failed:', err);
      toast.error('Reprint failed');
    } finally {
      setReprintingBoxId(null);
    }
  };

  const handleWeightModeChange = async (newMode: 'fixed' | 'catch' | 'scale') => {
    // Don't do anything if already in the requested mode
    if (newMode === activeWeightMode) return;

    try {
      // Persist the weight mode change to the database
      if (session.sub_session_number) {
        await stationManagementService.updateSubSessionWeightMode(session.sub_session_number, newMode);
      }

      // Record the weight mode change in session history
      const parentSessionNumber = session.sub_session_number?.split('-')[0] || String(session.session_number);
      await operatorService.recordSupervisorOverride({
        override_type: 'manual_weight',
        supervisor_id: supervisorId,
        supervisor_name: supervisorName,
        session_number: parentSessionNumber,
        sub_session_number: session.sub_session_number,
        operator_employee_number: operatorEmployeeNumber,
        operator_name: operatorName,
        reason: `Weight mode changed to ${newMode}`,
        details: {
          previous_mode: activeWeightMode,
          new_mode: newMode,
        },
      });

      onManualWeightToggle(newMode);
      setActiveWeightMode(newMode);
      // Note: Toast is shown by MobilePackerView via WebSocket event to avoid duplicate toasts
    } catch (err) {
      console.error('Weight mode change failed:', err);
      toast.error(t('supervisorOverride.weightModeChangeFailed') || 'Failed to change weight mode');
    }
  };

  const handleSignOff = async () => {
    setSigningOff(true);
    try {
      const parentSessionNumber = session.sub_session_number?.split('-')[0] || String(session.session_number);
      await operatorService.recordSupervisorOverride({
        override_type: 'yield_signoff',
        supervisor_id: supervisorId,
        supervisor_name: supervisorName,
        session_number: parentSessionNumber,
        sub_session_number: session.sub_session_number,
        operator_employee_number: operatorEmployeeNumber,
        operator_name: operatorName,
        reason: 'End of day yield sign-off',
        details: {
          input_weight: session.initial_material_weight,
          output_weight: session.actual_output_weight,
          waste_weight: session.waste_weight,
          // Store as whole % in DB for backward compat with OverrideHistoryTable
          yield_percentage: session.yield_percentage != null ? Number(session.yield_percentage) * 100 : undefined,
          expected_yield: session.expected_yield != null ? Number(session.expected_yield) * 100 : undefined,
        },
      });

      setSignedOff(true);
      toast.success(t('supervisorOverride.signOffSuccess'));
    } catch (err) {
      console.error('Sign-off failed:', err);
      toast.error('Sign-off failed');
    } finally {
      setSigningOff(false);
    }
  };

  const handleClose = () => {
    // Weight mode is now persisted to the database, so we don't reset it on close
    onClose();
  };

  if (!isOpen) return null;

  const reasons = [
    { value: 'damaged', label: t('supervisorOverride.reasons.damaged') },
    { value: 'misprint', label: t('supervisorOverride.reasons.misprint') },
    { value: 'illegible', label: t('supervisorOverride.reasons.illegible') },
    { value: 'wrongInfo', label: t('supervisorOverride.reasons.wrongInfo') },
    { value: 'other', label: t('supervisorOverride.reasons.other') },
  ];

  const allTabs = [
    { id: 'reprints' as TabType, label: t('supervisorOverride.labelReprints'), icon: Printer },
    { id: 'manualWeight' as TabType, label: t('supervisorOverride.weightMode') || 'Weight Mode', icon: Scale },
    { id: 'eodSummary' as TabType, label: t('supervisorOverride.endOfDaySummary'), icon: ClipboardCheck },
  ];

  // Filter out EOD tab if hideEodSummary is true
  const tabs = hideEodSummary ? allTabs.filter(tab => tab.id !== 'eodSummary') : allTabs;

  // Use fresh session data when available (more accurate), fallback to passed session
  const sessionData = freshSession || session;

  // Compute yield data for EOD tab
  // Count packed boxes and calculate output weight from actual box weights
  const packedBoxes = boxes.filter(box => box.box_status === 'packed');
  const boxCount = packedBoxes.length;

  // Calculate output weight from sum of packed box weights (matches what operator sees)
  // This is more accurate than sessionData.actual_output_weight which may not be in sync
  const calculatedOutputWeight = packedBoxes.reduce((sum, box) => sum + Number(box.actual_weight || 0), 0);

  const inputWeight = Number(sessionData.initial_material_weight) || 0;
  // Use calculated output weight from boxes (primary) or fall back to session data
  const outputWeight = calculatedOutputWeight > 0 ? calculatedOutputWeight : (Number(sessionData.actual_output_weight) || 0);
  // expected_yield is now decimal from service layer (e.g. 0.85)
  const expectedYield = Number(sessionData.expected_yield) || 0;

  // Calculate yield dynamically as decimal (e.g. 0.922 = 92.2%)
  const yieldPct = inputWeight > 0 ? outputWeight / inputWeight : 0;
  const variance = yieldPct - expectedYield;

  // Get waste and returns for weight breakdown summary
  const wasteWeight = Number(sessionData.waste_weight) || 0;
  const returnsWeight = Number(sessionData.remainder_intact) || 0;

  // Check if weights are balanced (Input = Waste + Returns + Output)
  const accountedWeight = outputWeight + wasteWeight + returnsWeight;
  const weightDifference = Math.abs(inputWeight - accountedWeight);
  const isWeightBalanced = weightDifference <= 0.1; // 0.1 lb tolerance

  // Calculate pace (boxes per hour)
  const getElapsedHours = () => {
    if (!sessionData.started_at) return 0;
    const startTime = new Date(sessionData.started_at).getTime();
    const now = Date.now();
    const totalElapsedMs = now - startTime;
    const pausedMs = (sessionData.paused_duration_seconds || 0) * 1000;
    // Cap paused time to total elapsed time (in case of bad data)
    const effectivePausedMs = Math.min(pausedMs, totalElapsedMs);
    const workingMs = totalElapsedMs - effectivePausedMs;
    return Math.max(0, workingMs) / (1000 * 60 * 60); // Convert to hours, never negative
  };
  const elapsedHours = getElapsedHours();
  const pace = elapsedHours > 0.01 ? boxCount / elapsedHours : 0; // Require at least ~36 seconds to avoid division issues

  const elapsedTimeFormatted = formatElapsedTime(elapsedHours);

  // Calculate progress % based on box count (boxes packed / expected boxes)
  // This matches the progress shown in the Packer View
  // expected_boxes priority: sessionData > session prop > calculate from target/unit weight
  const unitWeight = Number(sessionData.box_target_weight) || Number(session.box_target_weight) ||
                     Number(sessionData.product_unit_weight) || Number(session.product_unit_weight) || 0;
  const targetWeight = Number(sessionData.target_output_weight) || Number(session.target_output_weight) || 0;
  const calculatedExpectedBoxes = unitWeight > 0 ? Math.floor(targetWeight / unitWeight) : 0;
  const expectedBoxes = Number(sessionData.expected_boxes) || Number(session.expected_boxes) || calculatedExpectedBoxes;
  const progressPct = expectedBoxes > 0 ? (boxCount / expectedBoxes) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-100 dark:bg-gray-900 rounded-2xl w-full max-w-md md:max-w-lg max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header - Compact */}
        <div className="bg-white dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                <span className="text-lg">🔑</span>
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white">
                  {t('supervisorOverride.title')}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {supervisorName}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content — scrollable */}
        <div className="flex-1 overflow-y-auto p-3">
          {/* Option selection (when no tab is active on initial load, show card buttons) */}
          {activeTab === 'reprints' && !selectedBoxId && boxes.length === 0 && !loadingBoxes ? (
            // Show card-style options when no boxes
            <div className="space-y-2 max-w-md mx-auto">
              {/* Label Reprints Card */}
              <button
                onClick={() => setActiveTab('reprints')}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  activeTab === 'reprints'
                    ? 'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20'
                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                }`}
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <Printer className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-left flex-1">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white block">
                    {t('supervisorOverride.labelReprints')}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('sessionDetails.noBoxes')}
                  </span>
                </div>
              </button>

              {/* Weight Mode Card */}
              <button
                onClick={() => setActiveTab('manualWeight')}
                className="w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all active:scale-[0.98] border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 hover:border-amber-300 dark:hover:border-amber-700"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <Scale className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white block">
                    {t('supervisorOverride.weightMode') || 'Weight Mode'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">
                    {activeWeightMode === 'catch'
                      ? t('supervisorOverride.manualModeActive')
                      : activeWeightMode === 'scale'
                        ? (t('mobile.scaleWeight') || 'Scale Weight')
                        : t('supervisorOverride.enableCatchWeight')}
                  </span>
                </div>
                {activeWeightMode !== 'fixed' && (
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${activeWeightMode === 'scale' ? 'bg-teal-500' : 'bg-amber-500'}`} />
                )}
              </button>

              {/* End of Day Summary Card - only if not hidden */}
              {!hideEodSummary && (
                <button
                  onClick={() => setActiveTab('eodSummary')}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all active:scale-[0.98] border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 hover:border-green-300 dark:hover:border-green-700"
                >
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="text-left flex-1">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white block">
                      {t('supervisorOverride.endOfDaySummary')}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('supervisorOverride.reviewAndSignOff') || 'Review yield and sign off'}
                    </span>
                  </div>
                  {signedOff && (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  )}
                </button>
              )}
            </div>
          ) : (
            // Show actual tab content
            <div className="space-y-3">
              {/* Tab toggle buttons */}
              {(activeTab !== 'reprints' || boxes.length > 0 || loadingBoxes) && (
                <div className="flex gap-1 p-1 bg-gray-200 dark:bg-gray-800 rounded-lg">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-xs font-medium transition-all ${
                        activeTab === tab.id
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      <tab.icon className="w-5 h-5" />
                    </button>
                  ))}
                </div>
              )}

              {/* ──── TAB 1: Label Reprints ──── */}
              {activeTab === 'reprints' && (
                <LabelReprintsTab
                  boxes={boxes as any}
                  discardedLabels={discardedLabels as any}
                  loadingBoxes={loadingBoxes}
                  reasons={reasons}
                  onReprint={handleReprint as any}
                  onVoidLabel={handleVoidLabel}
                  reprintingBoxId={reprintingBoxId}
                  voidingBoxId={voidingBoxId}
                  reprintReason={reprintReason}
                  onReprintReasonChange={setReprintReason}
                  selectedBoxId={selectedBoxId}
                  onSelectedBoxIdChange={setSelectedBoxId}
                />
              )}

              {/* ──── TAB 2: Weight Mode Selection ──── */}
              {activeTab === 'manualWeight' && (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 text-center">
                    {t('supervisorOverride.selectWeightMode')}
                  </p>
                  <div className="flex gap-2">
                    {/* Fixed Weight Option */}
                    <button
                      onClick={() => handleWeightModeChange('fixed')}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all active:scale-[0.98] ${
                        activeWeightMode === 'fixed'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <p className={`font-semibold text-sm text-center ${activeWeightMode === 'fixed' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                        {t('mobile.fixedWeight')}
                      </p>
                    </button>

                    {/* Catch Weight Option */}
                    <button
                      onClick={() => handleWeightModeChange('catch')}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all active:scale-[0.98] ${
                        activeWeightMode === 'catch'
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-500'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <p className={`font-semibold text-sm text-center ${activeWeightMode === 'catch' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}`}>
                        {t('mobile.catchWeight')}
                      </p>
                    </button>

                    {/* Scale Weight Option */}
                    <button
                      onClick={() => handleWeightModeChange('scale')}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all active:scale-[0.98] ${
                        activeWeightMode === 'scale'
                          ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-500'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <p className={`font-semibold text-sm text-center ${activeWeightMode === 'scale' ? 'text-teal-600 dark:text-teal-400' : 'text-gray-700 dark:text-gray-300'}`}>
                        {t('mobile.scaleWeight')}
                      </p>
                    </button>
                  </div>
                </div>
              )}

              {/* ──── TAB 3: End of Day Summary ──── */}
              {activeTab === 'eodSummary' && !hideEodSummary && (
                <EndOfDaySummaryTab
                  elapsedTimeFormatted={elapsedTimeFormatted}
                  boxCount={boxCount}
                  pace={pace}
                  progressPct={progressPct}
                  inputWeight={inputWeight}
                  outputWeight={outputWeight}
                  wasteWeight={wasteWeight}
                  returnsWeight={returnsWeight}
                  isWeightBalanced={isWeightBalanced}
                  expectedYield={expectedYield}
                  yieldPct={yieldPct}
                  variance={variance}
                  signedOff={signedOff}
                  signingOff={signingOff}
                  supervisorName={supervisorName}
                  onSignOff={handleSignOff}
                />
              )}
            </div>
          )}
        </div>

        {/* Close button at bottom */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <button
            onClick={handleClose}
            className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium active:scale-[0.98] transition-transform text-sm"
          >
            {t('common.close') || 'Close'}
          </button>
        </div>
      </div>

      {/* Label Preview Modal (TEST MODE) */}
      <LabelPreviewModal
        isOpen={showLabelPreview}
        onClose={() => setShowLabelPreview(false)}
        labelHtml={labelPreviewHtml}
        copies={numberOfCopies}
      />
    </div>
  );
};

export default SupervisorOverrideModal;
